'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterState {
  priceRange: [number, number];
  condition: string[];
  sellerRating: number;
  shipping: string[];
}

interface FilterOptionsProps {
  onFilterChange: (filters: FilterState) => void;
}

const FilterOptions = ({ onFilterChange }: FilterOptionsProps) => {
  const [filters, setFilters] = useState<FilterState>({
    priceRange: [0, 1000],
    condition: [],
    sellerRating: 0,
    shipping: []
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const conditions = ['New', 'Used', 'Refurbished', 'Open Box'];
  const shippingOptions = ['Free Shipping', 'Expedited', 'Local Pickup'];

  const handleConditionToggle = (condition: string) => {
    const newConditions = filters.condition.includes(condition)
      ? filters.condition.filter(c => c !== condition)
      : [...filters.condition, condition];
    
    const newFilters = { ...filters, condition: newConditions };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleShippingToggle = (shipping: string) => {
    const newShipping = filters.shipping.includes(shipping)
      ? filters.shipping.filter(s => s !== shipping)
      : [...filters.shipping, shipping];
    
    const newFilters = { ...filters, shipping: newShipping };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handlePriceChange = (value: number, index: 0 | 1) => {
    const newPriceRange: [number, number] = [...filters.priceRange] as [number, number];
    newPriceRange[index] = value;
    const newFilters = { ...filters, priceRange: newPriceRange };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleRatingChange = (rating: number) => {
    const newFilters = { ...filters, sellerRating: rating === filters.sellerRating ? 0 : rating };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    const clearedFilters: FilterState = {
      priceRange: [0, 1000],
      condition: [],
      sellerRating: 0,
      shipping: []
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = 
    filters.condition.length + 
    filters.shipping.length + 
    (filters.sellerRating > 0 ? 1 : 0);

  return (
    <div className="bg-card rounded-lg shadow-elevation-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 transition-smooth hover:bg-muted"
      >
        <div className="flex items-center space-x-2">
          <Icon name="AdjustmentsHorizontalIcon" size={20} className="text-primary" />
          <span className="font-heading text-base font-semibold text-text-primary">
            Filter Results
          </span>
          {activeFilterCount > 0 && (
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </div>
        <Icon 
          name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
          size={20} 
          className="text-text-secondary" 
        />
      </button>

      {isExpanded && (
        <div className="border-t border-border p-4 space-y-6">
          <div className="space-y-3">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Price Range
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-text-secondary">Min</span>
                <span className="font-mono text-sm font-medium text-primary">
                  ${filters.priceRange[0]}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1000"
                step="50"
                value={filters.priceRange[0]}
                onChange={(e) => handlePriceChange(parseInt(e.target.value), 0)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-text-secondary">Max</span>
                <span className="font-mono text-sm font-medium text-primary">
                  ${filters.priceRange[1]}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1000"
                step="50"
                value={filters.priceRange[1]}
                onChange={(e) => handlePriceChange(parseInt(e.target.value), 1)}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Condition
            </h4>
            <div className="space-y-2">
              {conditions.map(condition => (
                <label key={condition} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.condition.includes(condition)}
                    onChange={() => handleConditionToggle(condition)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {condition}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Minimum Seller Rating
            </h4>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => handleRatingChange(rating)}
                  className="transition-smooth hover:scale-110 active:scale-95"
                  aria-label={`${rating} stars`}
                >
                  <Icon
                    name="StarIcon"
                    size={24}
                    variant={rating <= filters.sellerRating ? 'solid' : 'outline'}
                    className={rating <= filters.sellerRating ? 'text-accent' : 'text-muted-foreground'}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-6">
            <h4 className="font-caption text-xs font-medium uppercase tracking-wider text-text-secondary">
              Shipping Options
            </h4>
            <div className="space-y-2">
              {shippingOptions.map(shipping => (
                <label key={shipping} className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={filters.shipping.includes(shipping)}
                    onChange={() => handleShippingToggle(shipping)}
                    className="h-4 w-4 rounded border-border text-primary transition-smooth focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  />
                  <span className="font-body text-sm text-text-secondary group-hover:text-text-primary transition-smooth">
                    {shipping}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={handleClearAll}
            className="w-full rounded-lg border border-border px-4 py-2 font-body text-sm font-medium text-text-secondary transition-smooth hover:border-primary hover:text-primary active:scale-[0.97]"
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterOptions;